<template>
  <div>
    <div class="p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
    </div>
    <div class="p-6 pb-0">
      <div class="flex items-center gap-4 mb-4">
        <button @click="openAddUserModal" class="bg-blue-400 text-white px-4 py-2 rounded hover:bg-blue-500">{{ $t('an.gestione_clienti_fornitori.add_button') }}</button>
        <input 
          v-model="searchTerm" 
          @keyup.enter="performSearch"
          @blur="performSearch"
          type="text" 
          :placeholder="$t('an.gestione_clienti_fornitori.search_placeholder')" 
          class="p93__input_general px-4 py-2 min-w-[240px]" 
        />
        <div class="flex items-center gap-2">
          <label v-if="$policy('showClienti').canView" class="flex items-center cursor-pointer">
            <input 
              v-model="showClienti" 
              @change="handleClientiChange"
              type="checkbox" 
              class="sr-only"
            />
            <div class="relative">
              <div :class="showClienti ? 'bg-green-500' : 'bg-gray-600'" class="block w-14 h-8 rounded-full transition-colors"></div>
              <div :class="showClienti ? 'translate-x-6' : 'translate-x-0'" class="absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition"></div>
            </div>
            <span class="ml-2 text-sm">{{ $t('an.gestione_clienti_fornitori.customers') }}</span>
          </label>
          <label v-if="$policy('showFornitori').canView" class="flex items-center cursor-pointer">
            <input 
              v-model="showFornitori" 
              @change="handleFornitoriChange"
              type="checkbox" 
              class="sr-only"
            />
            <div class="relative">
              <div :class="showFornitori ? 'bg-green-500' : 'bg-gray-600'" class="block w-14 h-8 rounded-full transition-colors"></div>
              <div :class="showFornitori ? 'translate-x-6' : 'translate-x-0'" class="absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition"></div>
            </div>
            <span class="ml-2 text-sm">{{ $t('an.gestione_clienti_fornitori.suppliers') }}</span>
          </label>
        </div>
      </div>
      <table class="min-w-full bg-white rounded-md shadow-lg">
        <thead>
          <tr>
            <SortableTableHeader 
              :title="$t('an.fields.id')" 
              field="CustSupp" 
              :currentSortField="sortField" 
              :sortDirection="sortDirection" 
              @sort="sort" 
            />
            <SortableTableHeader 
              :title="$t('an.fields.company_name')" 
              field="CompanyName" 
              :currentSortField="sortField" 
              :sortDirection="sortDirection" 
              :isDefault="true"
              @sort="sort" 
            />
            <th class="py-2 px-4 border-b text-left">{{ $t('an.fields.type') }}</th>
            <th class="py-2 px-4 border-b text-center">{{ $t('an.fields.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in users.data" :key="user.id">
            <td class="py-2 px-4 border-b text-left">{{ user.CustSupp }}</td>
            <td class="py-2 px-4 border-b text-left">{{ user.CompanyName }}</td>
            <td class="py-2 px-4 border-b text-left">{{ user.Type }}</td>
            <td class="py-2 px-4 border-b text-center">
              <button @click="openDetailsUserModal(user)" class="bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600 mr-2">{{ $t('an.actions.details') }}</button>
              <button v-if="$policy('editButton').canView" :disabled="$policy('editButton').props.disabled" @click="openEditUserModal(user)" class="bg-yellow-400 disabled:bg-yellow-300 text-white px-2 py-1 rounded hover:bg-yellow-500 mr-2">{{ $t('an.actions.edit') }}</button>
              <button @click="openSediModal(user)" class="bg-blue-400 text-white px-2 py-1 rounded hover:bg-blue-500 mr-2">{{ $t('an.actions.sites') }}</button>
              <button v-if="collaborationEnabled" @click="openCollaborationModal(user)" class="bg-purple-500 text-white px-2 py-1 rounded hover:bg-purple-600">Collaboration</button>
            </td>
          </tr>
          <tr>
            <td class="text-left" colspan="4"><Pagination :links="users.links" /></td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modale per aggiungere/modificare utente -->
    <div v-if="showUserModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50" @click="closeUserModal" style="padding-top: 80px;">
      <div class="bg-white p-6 rounded-md w-full max-w-3xl max-h-[90vh] overflow-y-auto" @click.stop>
        <h2 class="text-xl mb-4">{{ isEditing ? $t('an.gestione_clienti_fornitori.modal_edit_title') : $t('an.gestione_clienti_fornitori.modal_add_title') }}</h2>
        <form @submit.prevent="isEditing ? updateUser() : addUser()">
          
          <div v-if="!isEditing" class="grid grid-cols-4 gap-4">
            <div class="mb-4 col-span-4">
              <label class="block text-gray-700">{{ $t('an.fields.type') }} <span class="text-red-500">*</span></label>
              <select v-model="userForm.CustSuppType" class="p93__input" required :readonly="isEditing" :disabled="isEditing">
                <option value="">{{ $t('an.actions.select_type') }}</option>
                <option v-for="(type, id) in filteredCustSuppTypes" :key="id" :value="id">{{ type }}</option>
              </select>
              <div v-if="errors.CustSuppType" class="text-red-800 text-sm mt-1">{{ errors.CustSuppType }}</div>
            </div>
          </div>
          <input v-else type="hidden" v-model="userForm.CustSuppType" />

          <div class="grid grid-cols-4 gap-4">
            <div class="mb-4 col-span-2">
              <label class="block text-gray-700">{{ $t('an.fields.company_name') }} <span class="text-red-500">*</span></label>
              <input v-model="userForm.CompanyName" type="text" class="p93__input" maxlength="128" required>
              <div v-if="errors.CompanyName" class="text-red-800 text-sm mt-1">{{ errors.CompanyName }}</div>
            </div>
            <div class="mb-4 col-span-2">
              <label class="block text-gray-700">{{ $t('an.fields.city') }}</label>
              <input v-model="userForm.City" type="text" class="p93__input" maxlength="64">
              <div v-if="errors.City" class="text-red-800 text-sm mt-1">{{ errors.City }}</div>
            </div>
          </div>

          <div class="grid grid-cols-4 gap-4">
            <div class="mb-4 col-span-4">
              <label class="block text-gray-700">{{ $t('an.fields.address') }}</label>
              <div class="flex items-center gap-2">
                <input v-model="userForm.Address" type="text" class="p93__input" maxlength="128" />
              </div>
              <div v-if="errors.Address" class="text-red-800 text-sm mt-1">{{ errors.Address }}</div>
            </div>
          </div>

          <div class="grid grid-cols-3 gap-4">
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('an.fields.zip_code') }}</label>
              <input v-model="userForm.ZIPCode" type="text" class="p93__input" maxlength="10">
              <div v-if="errors.ZIPCode" class="text-red-800 text-sm mt-1">{{ errors.ZIPCode }}</div>
            </div>
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('an.fields.county') }}</label>
              <input v-model="userForm.County" type="text" class="p93__input" maxlength="3">
              <div v-if="errors.County" class="text-red-800 text-sm mt-1">{{ errors.County }}</div>
            </div>
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('an.fields.country') }}</label>
              <input v-model="userForm.ISOCountryCode" type="text" class="p93__input" maxlength="2" placeholder="IT">
              <div v-if="errors.ISOCountryCode" class="text-red-800 text-sm mt-1">{{ errors.ISOCountryCode }}</div>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('an.fields.latitude') }}</label>
              <input v-model="userForm.Latitude" type="text" class="p93__input" maxlength="16">
              <div v-if="errors.Latitude" class="text-red-800 text-sm mt-1">{{ errors.Latitude }}</div>
            </div>
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('an.fields.longitude') }}</label>
              <input v-model="userForm.Longitude" type="text" class="p93__input" maxlength="16">
              <div v-if="errors.Longitude" class="text-red-800 text-sm mt-1">{{ errors.Longitude }}</div>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('an.fields.phone1') }}</label>
              <input v-model="userForm.Telephone1" type="text" class="p93__input" maxlength="20">
              <div v-if="errors.Telephone1" class="text-red-800 text-sm mt-1">{{ errors.Telephone1}}</div>
            </div>
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('an.fields.phone2') }}</label>
              <input v-model="userForm.Telephone2" type="text" class="p93__input" maxlength="20">
              <div v-if="errors.Telephone2" class="text-red-800 text-sm mt-1">{{ errors.Telephone2}}</div>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('ui.email') }} <span v-if="$policy('EMail').isRequired" class="text-red-500">*</span></label>
              <input v-model="userForm.EMail" type="email" class="p93__input" maxlength="128" v-bind="$policy('EMail').props">
              <div v-if="errors.EMail" class="text-red-800 text-sm mt-1">{{ errors.EMail }}</div>
            </div>
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('an.fields.email_certified') }}</label>
              <input v-model="userForm.CertifiedEMail" type="email" class="p93__input" maxlength="64">
              <div v-if="errors.CertifiedEMail" class="text-red-800 text-sm mt-1">{{ errors.CertifiedEMail }}</div>
            </div>
          </div>

          <div class="grid grid-cols-3 gap-4">
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('an.fields.vat') }} <span v-if="$policy('TaxIdNumber').isRequired" class="text-red-500">*</span></label>
              <input v-model="userForm.TaxIdNumber" type="text" class="p93__input" maxlength="20" v-bind="$policy('TaxIdNumber').props">
              <div v-if="errors.TaxIdNumber" class="text-red-800 text-sm mt-1">{{ errors.TaxIdNumber }}</div>
            </div>
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('an.fields.fiscal_code') }}</label>
              <input v-model="userForm.FiscalCode" type="text" class="p93__input" maxlength="20">
              <div v-if="errors.FiscalCode" class="text-red-800 text-sm mt-1">{{ errors.FiscalCode }}</div>
            </div>
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('an.fields.sdi_code') }} <span v-if="$policy('IPACode').isRequired" class="text-red-500">*</span></label>
              <input v-model="userForm.IPACode" type="text" class="p93__input" maxlength="7" v-bind="$policy('IPACode').props">
              <div v-if="errors.IPACode" class="text-red-800 text-sm mt-1">{{ errors.IPACode }}</div>
            </div>
          </div>

          <div class="grid grid-cols-4 gap-4">
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('an.fields.abi') }}</label>
              <input v-model="userForm.Abi" type="text" class="p93__input" maxlength="6">
              <div v-if="errors.Abi" class="text-red-800 text-sm mt-1">{{ errors.Abi }}</div>
            </div>
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('an.fields.cab') }}</label>
              <input v-model="userForm.Cab" type="text" class="p93__input" maxlength="6">
              <div v-if="errors.Cab" class="text-red-800 text-sm mt-1">{{ errors.Cab }}</div>
            </div>
            <div class="mb-4 col-span-2">
              <label class="block text-gray-700">{{ $t('an.fields.bank') }}</label>
              <input v-model="userForm.Bank" type="text" class="p93__input">
              <div v-if="errors.Bank" class="text-red-800 text-sm mt-1">{{ errors.Bank }}</div>
            </div>
          </div>

          <div class="grid grid-cols-4 gap-4">
            <div class="mb-4 col-span-2">
              <label class="block text-gray-700">{{ $t('an.fields.payment') }}</label>
              <v-select :options="foundPayments" :filterable="false" v-model="userForm.Payment" label="name"
                @search="searchPayments" required>
                <template #no-options="{ search, searching }">
                  <div v-if="searching">{{ $t('an.actions.searching') }}</div>
                  <div v-else-if="!search">{{ $t('an.actions.start_typing') }}</div>
                </template>
              </v-select>
              <div v-if="errors.Payment" class="text-red-800 text-sm mt-1">{{ errors.Payment }}</div>
            </div>
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('an.fields.typical_discount') }}</label>
              <input v-model="userForm.DiscountFormula" type="text" class="p93__input" maxlength="16">
              <div v-if="errors.DiscountFormula" class="text-red-800 text-sm mt-1">{{ errors.DiscountFormula }}</div>
            </div>
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('an.fields.account') }}</label>
              <input v-model="userForm.Account" type="text" class="p93__input" readonly disabled>
              <div v-if="errors.Account" class="text-red-800 text-sm mt-1">{{ errors.Account }}</div>
            </div>
          </div>

          <div class="grid grid-cols-4 gap-4">
            <div class="mb-4 col-span-2">
              <label class="block text-gray-700">{{ $t('an.fields.agent') }}</label>
              <select v-model="userForm.SalesPerson" class="p93__input_general disabled:bg-gray-100" :disabled="fixedAgent">
                <option value="">{{ $t('an.actions.choose_agent') }}</option>
                <option v-for="agent in salesPeople" :key="agent.id" :value="agent.id">{{ agent.name }}</option>
              </select>
              <div v-if="errors.SalesPerson" class="text-red-800 text-sm mt-1">{{ errors.SalesPerson }}</div>
            </div>
          </div>

          <div class="flex justify-end pt-4">
            <button type="button" @click="closeUserModal" class="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 mr-2">{{ $t('ui.cancel') }}</button>
            <button type="submit" class="bg-blue-400 text-white px-4 py-2 rounded hover:bg-blue-500">{{ isEditing ? $t('an.actions.update') : $t('an.actions.add') }}</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modale dettagli utente -->
    <div
      v-if="showUserDetailsModal"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
      @click="closeUserDetailsModal"
      style="padding-top: 80px;"
    >
      <div
      class="bg-white p-6 rounded-md w-full max-w-3xl max-h-[90vh] overflow-y-auto"
      @click.stop
      >
      <div class="grid grid-cols-4 gap-4">
        <div class="mb-4 col-span-4">
        <label class="block text-gray-700">{{ $t('an.fields.type') }}</label>
        <input v-model="userDetails.CustomerType" type="text" class="p93__input" disabled readonly>
        </div>
      </div>
      <div class="grid grid-cols-4 gap-4">
        <div class="mb-4 col-span-2">
        <label class="block text-gray-700">{{ $t('an.fields.company_name') }}</label>
        <input v-model="userDetails.CompanyName" type="text" class="p93__input" disabled readonly>
        </div>
        <div class="mb-4 col-span-2">
        <label class="block text-gray-700">{{ $t('an.fields.city') }}</label>
        <input v-model="userDetails.City" type="text" class="p93__input" disabled readonly>
        </div>
      </div>
      <div class="grid grid-cols-4 gap-4">
        <div class="mb-4 col-span-4">
        <label class="block text-gray-700">{{ $t('an.fields.address') }}</label>
        <div class="flex items-center gap-2">
          <input v-model="userDetails.Address" type="text" class="p93__input" disabled readonly />
        </div>
        </div>
      </div>
      <div class="grid grid-cols-3 gap-4">
        <div class="mb-4 col-span-1">
        <label class="block text-gray-700">{{ $t('an.fields.zip_code') }}</label>
        <input v-model="userDetails.ZIPCode" type="text" class="p93__input" disabled readonly>
        </div>
        <div class="mb-4 col-span-1">
        <label class="block text-gray-700">{{ $t('an.fields.county') }}</label>
        <input v-model="userDetails.County" type="text" class="p93__input" disabled readonly>
        </div>
        <div class="mb-4 col-span-1">
        <label class="block text-gray-700">{{ $t('an.fields.country') }}</label>
        <input v-model="userDetails.ISOCountryCode" type="text" class="p93__input" disabled readonly>
        </div>
      </div>
      <div class="grid grid-cols-2 gap-4">
        <div class="mb-4 col-span-1">
        <label class="block text-gray-700">{{ $t('an.fields.latitude') }}</label>
        <input v-model="userDetails.Latitude" type="text" class="p93__input" disabled readonly>
        </div>
        <div class="mb-4 col-span-1">
        <label class="block text-gray-700">{{ $t('an.fields.longitude') }}</label>
        <input v-model="userDetails.Longitude" type="text" class="p93__input" disabled readonly>
        </div>
      </div>
      <div class="grid grid-cols-2 gap-4">
        <div class="mb-4 col-span-1">
        <label class="block text-gray-700">{{ $t('an.fields.phone1') }}</label>
        <input v-model="userDetails.Telephone1" type="text" class="p93__input" disabled readonly>
        </div>
        <div class="mb-4 col-span-1">
        <label class="block text-gray-700">{{ $t('an.fields.phone2') }}</label>
        <input v-model="userDetails.Telephone2" type="text" class="p93__input" disabled readonly>
        </div>
      </div>
      <div class="grid grid-cols-2 gap-4">
        <div class="mb-4 col-span-1">
        <label class="block text-gray-700">{{ $t('ui.email') }}</label>
        <input v-model="userDetails.EMail" type="text" class="p93__input" disabled readonly>
        </div>
        <div class="mb-4 col-span-1">
        <label class="block text-gray-700">{{ $t('an.fields.email_certified') }}</label>
        <input v-model="userDetails.CertifiedEMail" type="text" class="p93__input" disabled readonly>
        </div>
      </div>
      <div class="grid grid-cols-3 gap-4">
        <div class="mb-4 col-span-1">
        <label class="block text-gray-700">{{ $t('an.fields.vat') }}</label>
        <input v-model="userDetails.TaxIdNumber" type="text" class="p93__input" disabled readonly>
        </div>
        <div class="mb-4 col-span-1">
        <label class="block text-gray-700">{{ $t('an.fields.fiscal_code') }}</label>
        <input v-model="userDetails.FiscalCode" type="text" class="p93__input" disabled readonly>
        </div>
        <div class="mb-4 col-span-1">
        <label class="block text-gray-700">{{ $t('an.fields.sdi_code') }}</label>
        <input v-model="userDetails.IPACode" type="text" class="p93__input" disabled readonly>
        </div>
      </div>
      <div class="grid grid-cols-4 gap-4">
        <div class="mb-4 col-span-1">
        <label class="block text-gray-700">{{ $t('an.fields.abi') }}</label>
        <input v-model="userDetails.Abi" type="text" class="p93__input" disabled readonly>
        </div>
        <div class="mb-4 col-span-1">
        <label class="block text-gray-700">{{ $t('an.fields.cab') }}</label>
        <input v-model="userDetails.Cab" type="text" class="p93__input" disabled readonly>
        </div>
        <div class="mb-4 col-span-2">
        <label class="block text-gray-700">{{ $t('an.fields.bank') }}</label>
        <input v-model="userDetails.Bank" type="text" class="p93__input" disabled readonly>
        </div>
      </div>
      <div class="grid grid-cols-4 gap-4">
        <div class="mb-4 col-span-2">
          <label class="block text-gray-700">{{ $t('an.fields.payment') }}</label>
          <input v-model="userDetails.PaymentDescription" type="text" class="p93__input" disabled readonly>
        </div>
        <div class="mb-4 col-span-1">
          <label class="block text-gray-700">{{ $t('an.fields.typical_discount') }}</label>
          <input v-model="userDetails.DiscountFormula" type="text" class="p93__input" disabled readonly>
        </div>
        <div class="mb-4 col-span-1">
          <label class="block text-gray-700">{{ $t('an.fields.account') }}</label>
          <input v-model="userDetails.Account" type="text" class="p93__input" disabled readonly>
        </div>
      </div>

      <div class="grid grid-cols-4 gap-4">
        <div class="mb-4 col-span-2">
          <label class="block text-gray-700">{{ $t('an.fields.agent') }}</label>
          <select v-model="userDetails.SalesPerson" class="p93__input_general disabled:bg-gray-100" disabled>
            <option value="">{{ $t('an.actions.choose_agent') }}</option>
            <option v-for="agent in salesPeople" :key="agent.id" :value="agent.id">{{ agent.name }}</option>
          </select>
          <div v-if="errors.SalesPerson" class="text-red-800 text-sm mt-1">{{ errors.SalesPerson }}</div>
        </div>
      </div>
      
      <div v-if="userDetails.Notes"  class="grid grid-cols-1 gap-4">
        <div class="mb-4 col-span-1">
          <label class="block text-gray-700">{{ $t('an.fields.customer_notes') }}</label>
          <textarea v-model="userDetails.Notes" class="p93__input" disabled readonly></textarea>
        </div>
      </div>
      <div class="flex justify-end">
        <button type="button" @click="closeUserDetailsModal" class="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 mr-2">{{ $t('an.actions.close') }}</button>
      </div>
      </div>
    </div>

    <!-- Modale sedi -->
    <div v-if="showSediModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50" @click="closeSediModal" style="padding-top: 80px;">
      <div class="bg-white p-6 rounded-md w-full max-w-5xl max-h-[95vh] overflow-y-auto" @click.stop>
        <h2 class="text-xl mb-4">{{ $t('an.gestione_clienti_fornitori.sites_of') }} {{ sediUser.CompanyName }}</h2>
        <table class="min-w-full bg-white rounded-md shadow">
          <thead>
            <tr>
              <th class="py-2 px-4 border-b text-left">{{ $t('an.fields.company_name') }}</th>
              <th class="py-2 px-4 border-b text-left">{{ $t('an.fields.address') }}</th>
              <th class="py-2 px-4 border-b text-left">{{ $t('an.fields.phone') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="sede in sediList" :key="sede.id">
              <td class="py-2 px-4 border-b">{{ sede.CompanyName }}</td>
              <td class="py-2 px-4 border-b">
                {{ sede.Address + ', ' + sede.City + ', ' + sede.County }}
                <span v-if="sede.Latitude && sede.Longitude" class="text-xs text-gray-500">
                  ({{ sede.Latitude }}, {{ sede.Longitude }})
                </span>
              </td>
              <td class="py-2 px-4 border-b">
                {{ sede.Telephone1 }}
              </td>
            </tr>
            <tr v-if="sediList.length === 0">
              <td colspan="5" class="py-2 px-4 text-center text-gray-500">{{ $t('an.gestione_clienti_fornitori.no_sites') }}</td>
            </tr>
          </tbody>
        </table>
        <div class="flex justify-end pt-4">
          <button type="button" @click="closeSediModal" class="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 mr-2">{{ $t('an.actions.close') }}</button>
        </div>
      </div>
    </div>
    <!-- Modale collaboration -->
    <div v-if="showCollaborationModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50" @click="closeCollaborationModal" style="padding-top: 80px;">
      <div class="bg-white p-6 rounded-md w-full max-w-2xl max-h-[90vh] flex flex-col" @click.stop>
        <h2 class="text-xl font-semibold mb-3 shrink-0">Collaboration — {{ collaborationUser.CompanyName }}</h2>

        <!-- Filtro date -->
        <div class="flex items-center gap-2 mb-4 shrink-0">
          <span class="text-xs text-gray-500">Dal</span>
          <input type="date" v-model="collaborationDateFrom" class="p93__input text-sm py-1" />
          <span class="text-xs text-gray-500">al</span>
          <input type="date" v-model="collaborationDateTo" class="p93__input text-sm py-1" />
        </div>

        <!-- Form aggiungi nota -->
        <div class="bg-gray-50 border border-gray-200 rounded-md p-3 mb-4 shrink-0">
          <div class="flex gap-2 items-start">
            <textarea v-model="activityNote" rows="2" placeholder="Aggiungi una nota..." class="p93__input flex-1 resize-none text-sm"></textarea>
            <div class="flex flex-col gap-1">
              <input type="datetime-local" v-model="activityDate" class="p93__input text-sm" />
              <button @click="submitActivity" :disabled="activitySaving || !activityNote.trim()" class="bg-purple-500 disabled:bg-purple-300 text-white px-3 py-1 rounded text-sm hover:bg-purple-600">
                {{ activitySaving ? '...' : 'Aggiungi' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Timeline -->
        <div class="overflow-y-auto flex-1 pr-1">
          <div v-if="collaborationLoading" class="text-center py-8 text-gray-400 text-sm">Caricamento...</div>
          <div v-else-if="!collaborationData || !collaborationData.timeline.length" class="text-center py-8 text-gray-400 text-sm italic">Nessuna attività</div>
          <div v-else class="relative pl-6">
            <div class="absolute left-2 top-0 bottom-0 w-px bg-gray-200"></div>
            <div v-for="item in collaborationData.timeline" :key="item.type + '-' + item.id" class="relative mb-5">
              <!-- Dot -->
              <div class="absolute -left-4 top-1 w-3 h-3 rounded-full border-2 border-white"
                :class="{
                  'bg-blue-500':   item.type === 'visita',
                  'bg-orange-500': item.type === 'ordine',
                  'bg-yellow-400': item.type === 'offerta',
                  'bg-green-500':  item.type === 'nota',
                  'bg-red-500':    item.type === 'ordine_mago',
                  'bg-violet-500': item.type === 'documento_mago',
                }">
              </div>
              <!-- Badge tipo + data + link -->
              <div class="flex items-center gap-2 mb-0.5 flex-wrap">
                <span class="text-xs font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded"
                  :class="{
                    'bg-blue-100 text-blue-700':     item.type === 'visita',
                    'bg-orange-100 text-orange-700': item.type === 'ordine',
                    'bg-yellow-100 text-yellow-700': item.type === 'offerta',
                    'bg-green-100 text-green-700':   item.type === 'nota',
                    'bg-red-100 text-red-700':       item.type === 'ordine_mago',
                    'bg-violet-100 text-violet-700': item.type === 'documento_mago',
                  }">
                  {{ { visita: 'Visita', ordine: 'Ordine Portale', offerta: 'Offerta Portale', nota: 'Nota', ordine_mago: 'Ordine Mago', documento_mago: 'Documento Vendita' }[item.type] }}
                </span>
                <span class="text-xs text-gray-400">{{ item.date ? (['ordine','offerta','ordine_mago','documento_mago'].includes(item.type) ? formatDateOnly(item.date) : formatDate(item.date)) : '—' }}</span>
                <span v-if="item.date_end" class="text-xs text-gray-400">→ {{ formatTime(item.date_end) }}</span>
                <a v-if="item.type === 'visita'"
                  :href="route('modules.visite-commerciali.calendario') + '?appointment=' + item.id"
                  target="_blank"
                  class="text-xs text-blue-500 hover:underline ml-auto">↗ Apri calendario</a>
                <a v-if="item.type === 'ordine' || item.type === 'offerta'"
                  :href="route('modules.ordini.documento', { id: item.id })"
                  target="_blank"
                  class="text-xs text-blue-500 hover:underline ml-auto">↗ Apri documento</a>
                <a v-if="item.type === 'ordine_mago' && item.meta && item.meta.portal_id"
                  :href="route('modules.ordini.documento', { id: item.meta.portal_id })"
                  target="_blank"
                  class="text-xs text-blue-500 hover:underline ml-auto">↗ Apri documento</a>
              </div>
              <!-- Contenuto -->
              <p class="text-sm text-gray-800 leading-snug">{{ item.title }}</p>
              <p v-if="item.subtitle" class="text-xs text-gray-500 mt-0.5">{{ item.subtitle }}</p>
              <!-- Operatori visita -->
              <p v-if="item.type === 'visita' && item.meta.operators && item.meta.operators.length"
                class="text-xs text-gray-400 mt-0.5">
                👤 {{ item.meta.operators.join(', ') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Registri collegati -->
        <div v-if="collaborationData && collaborationData.registries && collaborationData.registries.length" class="border-t border-gray-200 pt-3 mt-2 shrink-0">
          <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Registri che fanno riferimento al cliente</p>
          <div class="flex flex-wrap gap-2">
            <a
              v-for="reg in collaborationData.registries"
              :key="reg.id"
              :href="route('modules.registri-personalizzati.show', { registryId: reg.id }) + '?customer=' + collaborationUser.CustSupp"
              target="_blank"
              class="text-xs bg-teal-50 text-teal-700 border border-teal-200 px-2 py-1 rounded hover:bg-teal-100"
            >↗ {{ reg.name }}</a>
          </div>
        </div>

        <div class="flex justify-end pt-4 shrink-0">
          <button type="button" @click="closeCollaborationModal" class="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600">{{ $t('an.actions.close') }}</button>
        </div>
      </div>
    </div>
  </div>

  <ToastNotification :show="toast.show" :message="toast.message" :type="toast.type"
    @update:show="toast.show = $event" />
</template>

<script>
import { Link, router, useForm } from '@inertiajs/vue3';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import Pagination from '@/Components/Pagination.vue';
import SortableTableHeader from '@/Components/SortableTableHeader.vue';
import ToastNotification from '@/Components/ToastNotification.vue';

export default {
  props: ['users', 'errors', 'salesPeople', 'startAgent', 'fixedAgent', 'collaborationEnabled'],
  components: {
    Link,
    BreadCrumbs,
    Pagination,
    SortableTableHeader,
    ToastNotification,
  },
  data() {
    return {
      showClienti: this.$policy('showClienti').isHidden ? false : new URLSearchParams(window.location.search).get('showClienti') !== '0',
      showFornitori: this.$policy('showFornitori').isHidden ? false : new URLSearchParams(window.location.search).get('showFornitori') !== '0',
      sortField: new URLSearchParams(window.location.search).get('sort') || 'CustSupp',
      sortDirection: new URLSearchParams(window.location.search).get('direction') || 'desc',
      searchTerm: new URLSearchParams(window.location.search).get('search') || '',
      showUserModal: false,
      showUserDetailsModal: false,
      showSediModal: false,
      showCollaborationModal: false,
      isEditing: false,
      foundPayments: [],
      sediList: [],
      sediUser: {},
      collaborationUser: {},
      collaborationData: null,
      collaborationLoading: false,
      collaborationDateFrom: (() => { const d = new Date(); d.setFullYear(d.getFullYear() - 1); return d.toISOString().slice(0, 10); })(),
      collaborationDateTo: new Date().toISOString().slice(0, 10),
      activityNote: '',
      activityDate: new Date().toISOString().slice(0, 16),
      activitySaving: false,
      toast: { show: false, message: '', type: 'error' },
      userDetails: {},
      userForm: useForm({
        Abi: null,
        Address: '',
        Bank: '',
        Cab: '',
        CertifiedEMail: '',
        EMail: '',
        City: '',
        County: '',
        CompanyName: '',
        CustSupp: '',
        CustSuppType: '',
        DiscountFormula: '',
        FiscalCode: '',
        IPACode: '',
        ISOCountryCode: 'IT',
        Latitude: '',
        Longitude: '',
        Payment: null,
        Account: '',
        Telephone1: '',
        Telephone2: '',
        ZIPCode: '',
        TaxIdNumber: '',
        Type: '',
        SalesPerson: this.startAgent || ''
      }),
    };
  },
  computed: {
    navigation() {
      return [
        { name: this.$t('ui.dashboard'), href: route('dashboard') },
        { name: this.$t('an.breadcrumb.anagrafiche'), href: route('modules.home', 'anagrafiche') },
        { name: this.$t('an.breadcrumb.clienti_fornitori'), href: route('modules.anagrafiche.clienti-fornitori') }
      ];
    },
    filteredCustSuppTypes() {
      const types = this.$page.props.CustSuppTypes;
      if (!types) return {};
      const result = {};
      for (const [id, name] of Object.entries(types)) {
        if (id == this.MagoEnums.CustSupp_Type.Supplier && this.$policy('showFornitori').isHidden) continue;
        if (id == this.MagoEnums.CustSupp_Type.Customer && this.$policy('showClienti').isHidden) continue;
        result[id] = name;
      }
      return result;
    },
  },
  watch: {
    'userForm.Abi': function (newAbi) {
      this.fetchBankDescription();
    },
    'userForm.Cab': function (newCab) {
      this.fetchBankDescription();
    },
    'userForm.ISOCountryCode': function () {
      this.updateAccount();
    },
    'userForm.CustSuppType': function () {
      this.updateAccount();
    },
    collaborationDateFrom() {
      if (this.showCollaborationModal) this.loadCollaborationData();
    },
    collaborationDateTo() {
      if (this.showCollaborationModal) this.loadCollaborationData();
    },
  },
  methods: {
    sort(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = 'asc';
      }
      this.performSearch();
    },
    handleClientiChange() {
      if (!this.showClienti && !this.showFornitori) {
        if (this.$policy('showFornitori').canView) {
          this.showFornitori = true;
        } else {
          this.showClienti = true;
        }
      }
      this.performSearch();
    },
    handleFornitoriChange() {
      if (!this.showFornitori && !this.showClienti) {
        if (this.$policy('showClienti').canView) {
          this.showClienti = true;
        } else {
          this.showFornitori = true;
        }
      }
      this.performSearch();
    },

    performSearch() {
      router.get(route('modules.anagrafiche.clienti-fornitori'), {
        search: this.searchTerm,
        showClienti: this.showClienti ? '1' : '0',
        showFornitori: this.showFornitori ? '1' : '0',
        sort: this.sortField || 'CompanyName',
        direction: this.sortDirection || 'asc'
      }, { preserveState: true, replace: true });
    },
    openAddUserModal() {
      this.userForm.reset();
      this.userForm.ISOCountryCode = 'IT';
      this.isEditing = false;
      this.showUserModal = true;
    },
    openEditUserModal(user) {
      // Fetch detailed data for editing
      axios
        .get(route('modules.anagrafiche.cust-supp.dettagli', { custSupp: user.CustSupp, custSuppType: user.CustSuppType }))
        .then((response) => {
          const data = response.data;
          this.userForm.reset();
          this.userForm.id = user.id;
          this.userForm.CustSupp = data.CustSupp || '';
          this.userForm.CustSuppType = data.CustSuppType || '';
          this.userForm.CompanyName = data.CompanyName || '';
          this.userForm.City = data.City || '';
          this.userForm.Address = data.Address || '';
          this.userForm.ZIPCode = data.ZIPCode || '';
          this.userForm.County = data.County || '';
          this.userForm.ISOCountryCode = data.ISOCountryCode || 'IT';
          this.userForm.Telephone1 = data.Telephone1 || '';
          this.userForm.Telephone2 = data.Telephone2 || '';
          this.userForm.EMail = data.EMail || '';
          this.userForm.CertifiedEMail = data.CertifiedEMail || '';
          this.userForm.TaxIdNumber = data.TaxIdNumber || '';
          this.userForm.FiscalCode = data.FiscalCode || '';
          this.userForm.IPACode = data.IPACode || '';
          this.userForm.Abi = data.Abi || '';
          this.userForm.Cab = data.Cab || '';
          this.userForm.Bank = data.Bank || '';
          this.userForm.PaymentDescription = data.PaymentDescription || '';
          this.userForm.SalesPerson = data.SalesPerson || '';
          let paymentObj = null;
          this.userForm.Account = data.Account || '';
          if (data.Payment && data.PaymentDescription) {
            paymentObj = this.foundPayments.find(p => p.id == data.Payment);
            if (!paymentObj) {
              paymentObj = { id: data.Payment, name: data.PaymentDescription };
              this.foundPayments.push(paymentObj);
            }
          }
          this.userForm.Payment = paymentObj || null;
          this.userForm.DiscountFormula = data.DiscountFormula || '';
          this.userForm.Type = data.Type || '';
          this.fetchBankDescription();
          this.isEditing = true;
          this.showUserModal = true;
        })
        .catch((error) => {
          console.error('Error fetching user details:', error);
        });
    },
    openDetailsUserModal(user) {
      axios
        .get(route('modules.anagrafiche.cust-supp.dettagli', { custSupp: user.CustSupp, custSuppType: user.CustSuppType }))
        .then((response) => {
          this.userDetails = response.data;
          this.showUserDetailsModal = true;
        });
    },
    openSediModal(user) {
      this.sediUser = user;
      this.sediList = [];
      axios
        .get(route('modules.anagrafiche.sedi', { custSupp: user.CustSupp, custSuppType: user.CustSuppType }))
        .then((response) => {
          this.sediList = response.data;
          this.showSediModal = true;
        })
        .catch(() => {
          this.sediList = [];
        });
    },
    closeUserModal() {
      this.showUserModal = false;
    },
    closeUserDetailsModal() {
      this.showUserDetailsModal = false;
    },
    closeSediModal() {
      this.showSediModal = false;
      this.sediList = [];
      this.sediUser = {};
    },
    loadCollaborationData() {
      this.collaborationLoading = true;
      axios
        .get(route('modules.anagrafiche.collaboration', { custSupp: this.collaborationUser.CustSupp }), {
          params: { date_from: this.collaborationDateFrom, date_to: this.collaborationDateTo },
        })
        .then((response) => {
          this.collaborationData = response.data;
        })
        .catch(() => {
          this.showToast('Errore nel caricamento dei dati collaboration', 'error');
        })
        .finally(() => {
          this.collaborationLoading = false;
        });
    },
    openCollaborationModal(user) {
      this.collaborationUser = user;
      this.collaborationData = null;
      this.showCollaborationModal = true;
      this.loadCollaborationData();
    },
    closeCollaborationModal() {
      this.showCollaborationModal = false;
      this.collaborationUser = {};
      this.collaborationData = null;
      this.activityNote = '';
    },
    submitActivity() {
      if (!this.activityNote.trim() || this.activitySaving) return;
      this.activitySaving = true;
      axios
        .post(route('modules.anagrafiche.collaboration.store-activity', { custSupp: this.collaborationUser.CustSupp }), {
          note: this.activityNote,
          activity_date: this.activityDate,
        })
        .then(() => {
          this.activityNote = '';
          this.activityDate = new Date().toISOString().slice(0, 16);
          return this.loadCollaborationData();
        })
        .catch(() => {
          this.showToast('Errore nel salvataggio della nota', 'error');
        })
        .finally(() => {
          this.activitySaving = false;
        });
    },
    showToast(message, type = 'error') {
      this.toast = { show: true, message, type };
    },
    formatDate(iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      return d.toLocaleDateString('it-IT') + ' ' + d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    },
    formatDateOnly(iso) {
      if (!iso) return '—';
      return new Date(iso).toLocaleDateString('it-IT');
    },
    formatTime(iso) {
      if (!iso) return '';
      return new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    },
    addUser() {
      // Prepara il payload, assicurandoti che Payment sia l'id
      this.userForm.Payment = this.userForm.Payment ? this.userForm.Payment.id : null;
      this.userForm.post(route('modules.anagrafiche.cust-supp.aggiungi'), {
        onSuccess: () => {
          this.closeUserModal();
          this.performSearch();
        }
      });
    },
    updateUser() {
      this.userForm.Payment = this.userForm.Payment ? this.userForm.Payment.id : null;
      const queryParams = {
        search: this.searchTerm,
        showClienti: this.showClienti ? '1' : '0',
        showFornitori: this.showFornitori ? '1' : '0',
        sort: this.sortField || 'CompanyName',
        direction: this.sortDirection || 'asc',
        page: this.$page.props.users.current_page || 1
      };
      this.userForm.post(route('modules.anagrafiche.cust-supp.aggiorna', queryParams), {
        onSuccess: () => {
          this.closeUserModal();
        }
      });
    },
    fetchBankDescription() {
      if (this.isEditing && this.userForm.Abi && this.userForm.Cab) {
        axios.get(route('modules.anagrafiche.bancaDaAbiCab'), { params: { abi: this.userForm.Abi, cab: this.userForm.Cab } })
          .then((response) => {
            this.userForm.Bank = response.data.description || '';
          });
      }
    },
    searchPayments(search, loading) {
      if (search.length >= 2) {
        loading(true);
        clearTimeout(this.searchPaymentsTimeout);
        this.searchPaymentsTimeout = setTimeout(() => {
          axios
            .get(route('query.search-payment-terms-by-description', { description: search }))
            .then((response) => {
              this.foundPayments = response.data;
              loading(false);
            });
        }, 300);
      }
    },
    googleMapsUrl(address, city, county) {
      let q = [address, city, county].filter(Boolean).join(', ');
      return 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(q);
    },
    updateAccount() {
      const isIT = (this.userForm.ISOCountryCode || '').toUpperCase() === 'IT';
      const isCliente = this.userForm.CustSuppType == this.MagoEnums.CustSupp_Type.Customer;
      const isFornitore = this.userForm.CustSuppType == this.MagoEnums.CustSupp_Type.Supplier;
      if (isCliente) {
        this.userForm.Account = isIT ? '01011000' : '01012000';
      } else if (isFornitore) {
        this.userForm.Account = isIT ? '05021000' : '05022000';
      } else {
        this.userForm.Account = '';
      }
    },
  },
};
</script>
